import { useEffect, useRef } from 'react';
import { ControlledMenu, MenuItem } from '@szhsin/react-menu';

import { useObservable } from '@paradigm/utils/src/hooks';
import { useCounterpartiesService } from '@paradigm/features/src/unified-rfqs/contexts/counterparties';

import { createContextMenu, MenuProps } from '#/components/context-menu';

interface CounterpartyMenu {
  readonly counterpartyId: string;
}

export const [useCpsCtxMenu, CpsCtxMenuProvider] = createContextMenu({
  name: 'Counterparties',
  component: CpsCtxMenu,
  initialValue: { counterpartyId: '' },
});

function CpsCtxMenu(props: MenuProps<CounterpartyMenu>) {
  const { actions, state } = props;
  const { anchorPoint, counterpartyId } = state;
  const isOpen = anchorPoint != null;

  const cpsService = useCounterpartiesService();
  const currentCp = useObservable(cpsService.store.item(counterpartyId));

  const prevOpenCpRef = useRef(currentCp);
  useEffect(() => {
    if (isOpen) prevOpenCpRef.current = currentCp;
  });

  // On closing animation the menu should keep looking as it were
  // on the last render when it was open. This prevents for example
  // showing 'Unfavorite' when user clicks on 'Favorite'.
  const cp = isOpen ? currentCp : prevOpenCpRef.current;

  const isFavorite = cp?.isFavorite ?? false;

  const toggleFavorite = () => {
    if (counterpartyId === '') return;
    cpsService.setFavorite(counterpartyId, !isFavorite);
  };

  return (
    <ControlledMenu
      anchorPoint={anchorPoint}
      state={isOpen ? 'opening' : 'closing'}
      onClose={actions.close}
    >
      <MenuItem onClick={toggleFavorite}>
        {isFavorite ? 'Unfavorite' : 'Favorite'}
      </MenuItem>
    </ControlledMenu>
  );
}
